import { atom, useRecoilState } from 'recoil';
import * as z from 'zod';
import { SpaceTheme, SpaceThemeSchema } from '@cocoplatform/coco-rtc-shared';
import { httpClient } from '@cocoplatform/coco-rtc-client';
import keyBy from 'lodash/keyBy';
import { reportServerError } from 'utils/report-error';
import { useEffect, useMemo } from 'react';
import { browserStorage } from 'utils/browser-storage';
import { timeout } from 'utils/async';
import { msg, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';


// All, Multiplayer Games, Collaborative Stories, Interactive Art, Animations, Physical Computing, Video Sensing, Sound & Music, Science Simulations
export const getThemeLocalizedNames = () => ({
  all: t`All`,
  art: t`Art`,
  animation: t`Animation`,
  games: t`Games`,
  stories: t`Stories`,
  'chain reactions': t`Chain Reactions`,
  'video sensing': t`Video Sensing`,
  'music jam': t`Music Jam`,
  simulations: t`Simulations`,
  tutorials: t`Tutorials`,
  hardware: t`Hardware`,
  video: t`Video`,
  motion: t`Motion`,
  robotics: t`Robotics`,
  'multiplayer games': t`Multiplayer Games`,
  'collaborative stories': t`Collaborative Stories`,
  'interactive art': t`Interactive Art`,
  'physical computing': t`Physical Computing`,
  'sound & music': t`Sound & Music`,
  'science simulations': t`Science Simulations`,
  'animations': t`Animations`,
  'tutorial': t`Tutorial`,
});

export const defaultThemesState = {
  listQuery: '' as string,
  visibleThemeIds: [] as string[],
  themesById: {} as Record<string, SpaceTheme>,
};

export const spaceThemesAtom = atom({
  key: 'spaceThemes',
  default: defaultThemesState,
});

const normalizeName = (name: string) =>
  name
    .replaceAll(/[^a-z0-9 ]/gi, '')
    .trim()
    .toLowerCase();

const getCacheKey = (communityId: string) => {
  return `coco:u:$user:themes:${communityId}`;
};

const persistThemes = (
  communityId: string,
  themes: Record<string, SpaceTheme>,
) => {
  browserStorage.setItem(getCacheKey(communityId), JSON.stringify(themes));
};

const restoreThemes = (communityId: string) => {
  const rawThemes = browserStorage.getItem(getCacheKey(communityId));
  if (!rawThemes) return null;
  try {
    const parsedThemes = JSON.parse(rawThemes);
    return z.record(SpaceThemeSchema).parse(parsedThemes);
  } catch (e) {
    console.error(e);
    return null;
  }
};

let fetchP = Promise.resolve<void>(undefined);
let abortController = new AbortController();

const fetchThemes = (
  query: string,
  communityId?: string,
): Promise<SpaceTheme[]> => {
  abortController.abort();
  const curAbortController = new AbortController();
  abortController = curAbortController;
  const nextFetchP = fetchP.then(async () => {
    if (abortController !== curAbortController) return [];
    if (curAbortController.signal.aborted) return [];
    const resp = await httpClient.get(`/themes`, {
      params: {
        query,
        communityId,
      },
      signal: curAbortController.signal,
    });
    if (curAbortController.signal.aborted) return [];
    return resp.data.themes;
  });
  fetchP = nextFetchP
    .catch((e) => {
      console.error(e);
    })
    .then(() => timeout(500));
  return nextFetchP;
};

export const useThemeSelection = (opts: {
  communityId?: string;
  autoLoad?: boolean;
}) => {
  const { _ } = useLingui();
  const [themeState, setThemeState] = useRecoilState(spaceThemesAtom);

  useEffect(() => {
    if (opts.communityId && Object.keys(themeState.themesById).length > 0) {
      persistThemes(opts.communityId, themeState.themesById);
    }
  }, [themeState.themesById]);

  useEffect(() => {
    if (opts.autoLoad === false) return;
    const themesById =
      (opts.communityId ? restoreThemes(opts.communityId) : null) ?? {};
    setThemeState({
      ...defaultThemesState,
      themesById,
    });
    loadThemes('');
  }, [opts.communityId]);

  const loadThemes = async (query: string) => {
    try {
      const themes = await fetchThemes(query, opts.communityId);
      if (!themes) {
        return;
      }
      setThemeState((s) => {
        if (s.listQuery !== query) return s;
        const visibleThemeIds = themes.map((it) => it.id);
        const themesById = keyBy(themes, 'id');
        return {
          ...s,
          themesById: query ? { ...s.themesById, ...themesById } : themesById, // Replace current set so that locally persisted
          // themes don't linger around
          // after being deleted in remote
          visibleThemeIds,
        };
      });
    } catch (error: any) {
      if (error.message.match(/canceled/)) return;
      reportServerError({
        error,
        title: _(msg`Failed to fetch themes`),
      });
    }
  };

  const setListQuery = (query?: string) => {
    const listQuery = (query?.toLowerCase() ?? '').trim();
    const nQuery = normalizeName(listQuery);

    setThemeState((s) => {
      const visibleThemeIds = Object.values(s.themesById)
        .filter((it) => normalizeName(it.name).startsWith(nQuery))
        .map((it) => it.id);
      return {
        ...s,
        listQuery,
        visibleThemeIds,
      };
    });
    loadThemes(listQuery);
  };

  const setSelectedThemeIds = (themeIds: string[]) => {
    setThemeState((s) => ({
      ...s,
      selectedThemeIds: themeIds,
    }));
  };

  const internalThemes = useMemo(() => {
    return Object.values(themeState.themesById).filter((it) => {
      return it.isInternal;
    });
  }, [themeState.themesById]);

  const visibleThemes = useMemo(() => {
    if (!themeState.listQuery) return internalThemes;
    return themeState.visibleThemeIds.map((themeId) => {
      return themeState.themesById[themeId];
    });
  }, [
    internalThemes,
    themeState.listQuery,
    themeState.visibleThemeIds,
    themeState.themesById,
  ]);

  return {
    ...themeState,
    visibleThemes,
    setSelectedThemeIds,
    setListQuery,
    loadThemes,
    internalThemes,
  };
};
